import React, { useEffect } from "react";
import { Category } from "../../../types/form";
import type { FeedbackItemDetail } from "../../../API/feedbackAPI";
import { buttonStyleSm2 } from "../../../help/Utils";
import QuestionsList from "./QuestionsList";
import { FaLightbulb, FaSpinner } from "react-icons/fa";
import {
  FORM_SCROLL_TO_QUESTION_EVENT,
  type FormScrollToQuestionDetail,
} from "../formQuestionScroll";

interface FormContentProps {
  activeCategory: Category | null;
  formData: Record<string, any>;
  /** Form-wide visible question ids, transitive (client worklist #84). */
  visibleIds?: Set<number>;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  handleChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => void;
  handleMultipleEntryChange: (
    inputName: string,
    idx: number,
    value: any,
  ) => void;
  handleAddEntry: (inputName: string) => void;
  handleRemoveEntry: (inputName: string, entryIdx: number) => void;
  handleCompositeFieldChange: (
    inputName: string,
    compositeAnswer: Record<string, any>,
    fieldKey: string,
    value: any,
  ) => void;
  handleMultipleCompositeChange: (
    inputName: string,
    entryIdx: number,
    fieldKey: string,
    value: any,
  ) => void;
  isLoading: boolean;
  loadError: string | null;
  handleSubmit: () => void;
  handleBack?: () => void;
  isSaving: boolean;
  applicationId: number | undefined;
  formId: number | undefined;
  validationErrors?: Record<string, string>;
  isLastCategory?: boolean;
  onOpenHintsWithHint?: (
    hint: { title?: string; content: string } | null,
  ) => void;
  questionIdsWithFeedback?: Set<number>;
  onOpenFeedbackWithQuestion?: (questionId: number) => void;
  /** When true, only questions in `questionIdsWithPendingFeedback` are editable. */
  restrictEditsToFeedbackFields?: boolean;
  questionIdsWithPendingFeedback?: Set<number>;
  /** Entire form read-only (e.g. under attorney review / post-submission). */
  isFormViewOnly?: boolean;
  activeFeedbackItem?: FeedbackItemDetail | null;
  canGoBack?: boolean;
}

const FormContent: React.FC<FormContentProps> = ({
  activeCategory,
  formData,
  visibleIds,
  setFormData,
  handleChange,
  handleMultipleEntryChange,
  handleAddEntry,
  handleRemoveEntry,
  handleCompositeFieldChange,
  handleMultipleCompositeChange,
  isLoading,
  loadError,
  handleSubmit,
  handleBack,
  isSaving,
  applicationId,
  formId,
  validationErrors,
  isLastCategory = false,
  onOpenHintsWithHint,
  questionIdsWithFeedback,
  onOpenFeedbackWithQuestion,
  restrictEditsToFeedbackFields = false,
  questionIdsWithPendingFeedback,
  isFormViewOnly = false,
  activeFeedbackItem,
  canGoBack = false,
}) => {
  // Reset window scroll position when active category changes (instant so it
  // doesn't race with the subsequent scroll-to-question animation).
  useEffect(() => {
    if (activeCategory) {
      window.scrollTo({ top: 0 });
      const root = document.getElementById("form-questions-scroll-root");
      if (root) root.scrollTop = 0;
    }
  }, [activeCategory]);

  useEffect(() => {
    const SCROLL_RETRY_MS = 380;
    const MAX_SCROLL_ATTEMPTS = 5;

    const HIGHLIGHT_STYLE_ID = "lgv-form-question-highlight-keyframes";
    const HIGHLIGHT_CLASS = "lgv-form-question-highlight";

    const ensureHighlightStyles = () => {
      if (document.getElementById(HIGHLIGHT_STYLE_ID)) return;
      const style = document.createElement("style");
      style.id = HIGHLIGHT_STYLE_ID;
      // Ring + wash sit on ::before with negative inset so they don’t hug the question text.
      // No opacity animation on the block — keeps inputs readable.
      style.textContent = `
        .${HIGHLIGHT_CLASS} {
          overflow: visible;
        }
        .${HIGHLIGHT_CLASS}::before {
          content: "";
          position: absolute;
          inset: -8px;
          border-radius: 4px;
          pointer-events: none;
          z-index: -1;
          background-color: rgba(239, 246, 255, 0.62);
          box-shadow:
            0 0 0 2px rgba(27, 54, 93, 0.42),
            0 0 0 4px rgba(27, 54, 93, 0.045),
            0 4px 16px rgba(27, 54, 93, 0.1);
          animation: lgvFormQuestionHighlightRing 0.95s ease-in-out 2;
        }
        @keyframes lgvFormQuestionHighlightRing {
          0%, 100% {
            box-shadow:
              0 0 0 2px rgba(27, 54, 93, 0.42),
              0 0 0 4px rgba(27, 54, 93, 0.045),
              0 4px 16px rgba(27, 54, 93, 0.1);
            background-color: rgba(239, 246, 255, 0.62);
          }
          50% {
            box-shadow:
              0 0 0 3px rgba(27, 54, 93, 0.52),
              0 0 0 8px rgba(27, 54, 93, 0.035),
              0 8px 16px rgba(27, 54, 93, 0.14);
            background-color: rgba(219, 234, 254, 0.52);
          }
        }
      `;
      document.head.appendChild(style);
    };

    const applyHighlight = (el: HTMLElement, highlight: boolean) => {
      if (!highlight) return;
      ensureHighlightStyles();

      const prevInline = {
        position: el.style.position,
        overflow: el.style.overflow,
        zIndex: el.style.zIndex,
      };
      const computedPosition = window.getComputedStyle(el).position;
      const needRelative = computedPosition === "static";

      el.classList.add(HIGHLIGHT_CLASS);
      if (needRelative) {
        el.style.position = "relative";
        el.style.zIndex = prevInline.zIndex || "0";
      }
      el.style.overflow = "visible";

      const onEnd = (ev: AnimationEvent) => {
        if (ev.animationName !== "lgvFormQuestionHighlightRing") return;
        el.removeEventListener("animationend", onEnd);
        el.classList.remove(HIGHLIGHT_CLASS);
        el.style.position = prevInline.position;
        el.style.overflow = prevInline.overflow;
        if (needRelative) {
          el.style.zIndex = prevInline.zIndex;
        }
      };

      el.addEventListener("animationend", onEnd);
    };

    const scrollElIntoFormColumn = (el: HTMLElement) => {
      const root = document.getElementById("form-questions-scroll-root");
      // Only use root.scrollTo when the column is genuinely independently
      // scrollable (has overflow and content taller than itself). In the
      // normal page layout the root div has no max-height so it expands with
      // content and root.scrollTo is a no-op — fall through to scrollIntoView
      // which scrolls the window instead.
      if (root && root.scrollHeight > root.clientHeight + 4) {
        const er = el.getBoundingClientRect();
        const rr = root.getBoundingClientRect();
        const delta = er.top - rr.top + root.scrollTop;
        const targetTop =
          delta - root.clientHeight / 2 + Math.min(el.clientHeight, 120) / 2;
        root.scrollTo({
          top: Math.max(0, targetTop),
          behavior: "smooth",
        });
      } else {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    };

    const onScrollToQuestion = (ev: Event) => {
      const e = ev as CustomEvent<FormScrollToQuestionDetail>;
      const questionId = e.detail?.questionId;
      if (typeof questionId !== "number" || questionId <= 0) return;

      const highlight = e.detail?.highlight !== false;
      const delayMs =
        typeof e.detail?.delayMs === "number" && e.detail.delayMs > 0
          ? e.detail.delayMs
          : 0;

      const runAttempts = (attempt: number) => {
        const el = document.querySelector<HTMLElement>(
          `[data-question-id="${questionId}"]`
        );
        if (!el) {
          if (attempt < MAX_SCROLL_ATTEMPTS) {
            window.setTimeout(() => runAttempts(attempt + 1), SCROLL_RETRY_MS);
          }
          return;
        }
        scrollElIntoFormColumn(el);
        applyHighlight(el, highlight);
      };

      if (delayMs > 0) {
        window.setTimeout(() => runAttempts(0), delayMs);
      } else {
        window.requestAnimationFrame(() => runAttempts(0));
      }
    };

    window.addEventListener(
      FORM_SCROLL_TO_QUESTION_EVENT,
      onScrollToQuestion as EventListener
    );
    return () => {
      window.removeEventListener(
        FORM_SCROLL_TO_QUESTION_EVENT,
        onScrollToQuestion as EventListener
      );
    };
  }, []);

  return (
    <div className="transition-all duration-300 px-4 py-4 bg-white shadow-[0px_1px_2px_0px_#0000000D] rounded-lg">
      <div
        id="form-questions-scroll-root"
        className="md:min-h-[calc(80vh-120px)] md:overflow-y-auto scrollbar-thin scrollbar-thumb-slate-500 scrollbar-track-transparent flex flex-col justify-start gap-5 px-4"
      >
        {/* Category description/instructions moved to Smart Form Guide sidebar for a cleaner viewer */}
        {/* <div className="text-[#1B365D] text-base mb-5">
          <MarkdownRenderer
            content={
              activeCategory?.description ||
              activeCategory?.instructions ||
              "Please complete all required fields."
            }
          />
        </div> */}
        {onOpenHintsWithHint && (
          <div className="flex justify-end -mb-4 mt-2">
            <button
              type="button"
              onClick={() => onOpenHintsWithHint(null)}
              className="text-sm px-2 py-1.5 rounded-md bg-[#1B365D] text-white hover:bg-[#152a4a] transition-colors flex items-center justify-center gap-2"
            >
              <FaLightbulb />
              Hints
            </button>
          </div>
        )}
        {isFormViewOnly && (
          <div
            className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800"
            role="status"
          >
            This form is read-only while your application is under review. You can still use
            Hints and Feedback on the right when available.
          </div>
        )}
        {restrictEditsToFeedbackFields && !isFormViewOnly && (
          <div
            className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
            role="status"
          >
            {(questionIdsWithPendingFeedback?.size ?? 0) > 0
              ? "Reviewer feedback is open for this form. You can only edit questions that have pending notes; resolve each item in the feedback panel when you are done."
              : "All notes resolved. Submit your addressed feedback using the feedback panel."}
          </div>
        )}
        <QuestionsList
          visibleIds={visibleIds}
          activeCategory={activeCategory}
          formData={formData}
          setFormData={setFormData}
          handleChange={handleChange}
          handleMultipleEntryChange={handleMultipleEntryChange}
          handleAddEntry={handleAddEntry}
          handleRemoveEntry={handleRemoveEntry}
          handleCompositeFieldChange={handleCompositeFieldChange}
          handleMultipleCompositeChange={handleMultipleCompositeChange}
          isLoading={isLoading}
          loadError={loadError}
          validationErrors={validationErrors}
          onOpenHintsWithHint={onOpenHintsWithHint}
          questionIdsWithFeedback={questionIdsWithFeedback}
          onOpenFeedbackWithQuestion={onOpenFeedbackWithQuestion}
          restrictEditsToFeedbackFields={restrictEditsToFeedbackFields}
          questionIdsWithPendingFeedback={questionIdsWithPendingFeedback}
          isFormViewOnly={isFormViewOnly}
          activeFeedbackItem={activeFeedbackItem}
          applicationId={applicationId}
        />
        <div className="flex items-center justify-between gap-3 mt-auto">
          <div>
            {canGoBack && handleBack && (
              <button
                type="button"
                onClick={handleBack}
                disabled={isSaving}
                className="rounded-md disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px] min-h-[50px] flex items-center justify-center"
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "50px",
                  padding: "12px 18px",
                  fontSize: "16px",
                  color: "#1B365D",
                  border: "1px solid #1B365D",
                  fontWeight: 700,
                }}
              >
                Back
              </button>
            )}
          </div>
          {/* The bottom Save button saves the current section's answers.
              On the last section, clicking it also triggers the parent
              StepperForm to open the SubmitConfirmationModal (after
              save + readiness check) so the user can confirm submission. */}
          <button
            onClick={handleSubmit}
            disabled={isFormViewOnly || isSaving || !applicationId || !formId}
            className="rounded-md disabled:opacity-50 disabled:cursor-not-allowed min-w-[150px] min-h-[50px] flex items-center justify-center gap-2"
            style={buttonStyleSm2}
          >
            {isSaving ? (
              <FaSpinner className="animate-spin" />
            ) : isLastCategory ? (
              "Save & Submit"
            ) : (
              "Save & Next"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormContent;
